import {
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  SpaceBetween,
  StatusIndicator,
  Table,
  Textarea,
} from '@cloudscape-design/components';
import { ProcessNames } from '@parallel-fluidics/shared/constants';
import { useNotifications } from 'features/notifications';
import {
  PRESS_HMI_BONDING_CONFIG_FIELDS,
  PRESS_HMI_FORMING_CONFIG_FIELDS,
} from 'features/plc/contracts/pressHMI.fields';
import {
  collection,
  doc,
  getFirestore,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link } from 'react-router-dom';

import UserEmail from '../Display/userEmail';
import parseTime from '../HMI/components/parseTime';
import { useProcessData } from './processDataProvider';

function ExpandableParametersTable({
  rows,
  plcConfig,
  instronMetadata = null,
}) {
  const [expandedItems, setExpandedItems] = useState([]);

  const items = useMemo(() => {
    const retItems = rows.map((row) => ({
      variable: row.header,
      key: row.header,
      value: '',
      children: row.values.map((item) => {
        const { handle, type, label } = item;
        let value = plcConfig[handle];
        if (value === undefined) {
          return null;
        }
        if (type === 'TIME') {
          value = parseTime(value);
        } else if (type === 'BOOL') {
          value = value ? 'On' : 'Off';
        }
        return {
          variable: label,
          key: handle,
          handle,
          value,
        };
      }).filter(Boolean),
    }));
    if (instronMetadata) {
      retItems.push({
        variable: 'Instron Data',
        key: 'Instron Data',
        children: Object.entries(instronMetadata).map(([key, value]) => ({
          variable: key,
          key,
          value,
        })).sort((a, b) => a.variable.localeCompare(b.variable)),
      });
    }
    return retItems;
  }, [rows, plcConfig, instronMetadata]);

  return (
    <Table
      header={<Header>HMI Parameters</Header>}
      expandableRows={{
        getItemChildren: (item) => item.children ?? [],
        isItemExpandable: (item) => item.children?.length > 0,
        expandedItems,
        onExpandableItemToggle: ({ detail }) => setExpandedItems((prev) => {
          const next = new Set(
            (prev ?? []).map((item) => item.key),
          );
          if (detail.expanded) {
            next.add(detail.item.key);
          } else {
            next.delete(detail.item.key);
          }
          return [...next].map((key) => ({ key }));
        }),
      }}
      columnDefinitions={[
        {
          id: 'variable',
          header: 'Variable',
          cell: (e) => e.variable,
          isRowHeader: true,
          width: '30%',
          maxWidth: '30%',
        },
        {
          id: 'handle',
          header: 'Handle',
          cell: (e) => e.handle,
          width: '40%',
          maxWidth: '40%',
        },
        {
          id: 'value',
          header: 'Value',
          cell: (e) => e.value,
          width: '30%',
          maxWidth: '30%',
        },
      ]}
      items={items}
      trackBy="key"
    />
  );
}

ExpandableParametersTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    header: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(PropTypes.shape({
      handle: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })),
  })).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  plcConfig: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  instronMetadata: PropTypes.object,
};

function HMIParameters() {
  const {
    session,
    instronData,
  } = useProcessData();
  const [configSnapshot, setConfigSnapshot] = useState([]);
  const [status, setStatus] = useState('loading');

  // load process_configs fields for TM2, skip for TM3
  useEffect(() => {
    if (!session?.process || !session?.plcConfig?.version) return undefined;
    const unsubscribe = onSnapshot(
      query(
        collection(getFirestore(), 'process_configs'),
        where('version', '==', session.plcConfig.version),
        where('processType', '==', session.process),
      ),
      (snapshot) => {
        const data = snapshot.docs.map((d) => d.data());
        setConfigSnapshot(data);
        setStatus('success');
      },
      () => {
        setStatus('error');
      },
    );

    return () => unsubscribe();
  }, [session?.plcConfig?.version, session?.process]);

  const { config, configErr } = useMemo(() => {
    if (!configSnapshot || configSnapshot.length === 0) {
      return { configErr: `No ${session.process} config found with a matching version ${session?.plcConfig?.version}. Showing raw data.` };
    }
    if (configSnapshot.length > 1) {
      return { configErr: `More than one ${session.process} config found with a matching version ${session?.plcConfig?.version}. Showing raw data.` };
    }

    const configData = { ...configSnapshot[0] };
    const sections = [];
    const sectionIndices = new Map();
    configData.handles.forEach((handle) => {
      if (!sectionIndices.has(handle.section)) {
        sectionIndices.set(handle.section, sections.length);
        sections.push({ header: handle.section, values: [] });
      }
    });
    configData.handles.forEach((handle) => {
      const sectionIndex = sectionIndices.get(handle.section);
      sections[sectionIndex].values.push(handle);
    });
    return { config: { ...configData, rows: sections } };
  }, [session, configSnapshot]);

  const { addNotification, removeNotification } = useNotifications();
  useEffect(() => {
    if (configErr) {
      addNotification({
        header: 'Error',
        type: 'error',
        content: configErr,
        dismissible: false,
        id: `${session.id}-configErr`,
      });
    } else {
      removeNotification(`${session.id}-configErr`);
    }
  }, [addNotification, configErr, removeNotification, session.id]);

  if (status === 'loading') {
    return (
      <Container header={<Header>Parameters</Header>}>
        <StatusIndicator type="loading">Loading...</StatusIndicator>
      </Container>
    );
  }

  if (config?.rows) {
    return (
      <ExpandableParametersTable
        rows={config.rows}
        plcConfig={session.plcConfig}
        instronMetadata={instronData?.[0]?.metadata}
      />
    );
  }
  return (
    <Table
      header={<Header>HMI Parameters</Header>}
      columnDefinitions={[
        {
          id: 'system',
          header: 'System',
          cell: (e) => e.system,
          isRowHeader: true,
        },
        {
          id: 'handle',
          header: 'Variable',
          cell: (e) => e.handle,
          isRowHeader: true,
        },
        {
          id: 'value',
          header: 'Value',
          cell: (e) => e.value,
        },
      ]}
      items={[
        ...Object.keys(session.plcConfig || {}).map((key) => (
          { system: 'PLC', handle: key, value: session.plcConfig[key] }
        )),
        ...Object.keys(instronData?.[0]?.metadata || {}).map((key) => (
          { system: 'Instron', handle: key, value: instronData?.[0]?.metadata[key] }
        )),
      ]}
    />
  );
}

function Overview() {
  const { session, instronData, isTM3 } = useProcessData();
  const tm3ConfigFields = useMemo(
    () => (session.process === ProcessNames.BONDING
      ? PRESS_HMI_BONDING_CONFIG_FIELDS
      : PRESS_HMI_FORMING_CONFIG_FIELDS),
    [session.process],
  );

  const initialValues = {
    hypothesis: session.hypothesis,
    observations: session.observations,
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validateOnChange={false}
      onSubmit={async (values) => {
        await updateDoc(doc(getFirestore(), 'process_logs', session.id), {
          hypothesis: values.hypothesis,
          observations: values.observations,
        });
      }}
    >
      {({
        values, handleSubmit, setFieldValue, isSubmitting, dirty,
      }) => (
        <SpaceBetween size="l">
          <Container header={(
            <Header
              actions={(
                <Button
                  variant="primary"
                  disabled={!dirty || isSubmitting}
                  onClick={handleSubmit}
                >
                  Save
                </Button>
              )}
            >
              {`${session.project}${session.pPart ? `-${session.pPart}` : ''} - ${session.number}`}
            </Header>
          )}
          >
            <SpaceBetween size="s">
              {session.travelers.length > 0 && (
                <SpaceBetween direction="horizontal" size="s">
                  <span key="title">{`Traveler${session.travelers.length > 1 ? 's' : ''}:`}</span>
                  {session.travelers.map((id) => (
                    <Link
                      key={id}
                      to={`/travelers/${id}`}
                      target="_blank"
                    >
                      {`${id}${session.toolMap?.[id] ? ` (${session.toolMap[id]})` : ''}`}
                    </Link>
                  ))}
                </SpaceBetween>
              )}
              <ColumnLayout columns={2}>
                <FormField label="Hypothesis" stretch>
                  <Textarea
                    value={values.hypothesis}
                    rows={3}
                    onChange={({ detail }) => { setFieldValue('hypothesis', detail.value); }}
                  />
                </FormField>
                <FormField label="Observations" stretch>
                  <Textarea
                    value={values.observations}
                    rows={3}
                    onChange={({ detail }) => { setFieldValue('observations', detail.value); }}
                  />
                </FormField>
              </ColumnLayout>
              <ColumnLayout columns={4}>
                {session.stepPPartId && (
                  <FormField label="Step Part ID">
                    {session.stepPPartId}
                  </FormField>
                )}
                {session.process === ProcessNames.FORMING && (
                  <>
                    <FormField label="Material">
                      {session.material}
                    </FormField>
                    <FormField label="Piston">
                      {session.piston}
                    </FormField>
                    <FormField label="Tool">
                      {session.tool}
                    </FormField>
                  </>
                )}
                <FormField label="Press">
                  {session.press}
                </FormField>
                <FormField label="Production">
                  {session.production ? 'Yes' : 'No'}
                </FormField>
                <FormField label="Operator">
                  <UserEmail userId={session.user} />
                </FormField>
                <FormField label="Process">
                  {session.process}
                </FormField>
                {session.process === ProcessNames.BONDING && (
                  <>
                    <FormField label="Number Of Chips">
                      {session.numberOfChips}
                    </FormField>
                    <FormField label="Press Orientation">
                      {session.pressOrientation}
                    </FormField>
                  </>
                )}
                {!!session?.capMaterial && (
                  <FormField label="Cap Material">
                    {session.capMaterial}
                  </FormField>
                )}
                {!!session?.mass && (
                  <FormField label="Mass">
                    {session.mass}
                  </FormField>
                )}
              </ColumnLayout>
            </SpaceBetween>
          </Container>
          {!!session?.process && (
            isTM3 ? (
              <ExpandableParametersTable
                rows={tm3ConfigFields.reduce((sections, field) => {
                  let section = sections.find((item) => item.header === field.section);
                  if (!section) {
                    section = { header: field.section, values: [] };
                    sections.push(section);
                  }
                  section.values.push({
                    handle: field.key,
                    type: field.type,
                    label: field.group ? `${field.group} ${field.label}` : field.label,
                  });
                  return sections;
                }, [])}
                plcConfig={session.plcConfig}
                instronMetadata={instronData?.[0]?.metadata}
              />
            ) : (
              <HMIParameters />
            )
          )}
        </SpaceBetween>
      )}
    </Formik>
  );
}

export default Overview;
